import axios from "axios";
import authApi from "./authApi";

const baseUrl = process.env.REACT_APP_BASE_URL;

const api = axios.create({
    baseURL: baseUrl,
    withCredentials: true // KRITIS: Agar cookie (refresh token) ikut terkirim
});

// Flag untuk mencegah infinite loop refresh
let isRefreshing = false;

// Antrian request yang gagal 401 saat refresh sedang berjalan.
// Mereka akan di-retry otomatis begitu token baru didapat,
// sehingga request paralel (mis. categories + pockets) tidak ada
// yang hilang ketika keduanya kena 401 bersamaan.
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token);
        }
    });
    failedQueue = [];
};

// Add a request interceptor
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // Pastikan cookie ikut terkirim
    config.withCredentials = true;
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Add a response interceptor
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Guard: pastikan error.response ada
        if (!error.response || !originalRequest) {
            return Promise.reject(error);
        }

        // Jangan coba refresh untuk request ke endpoint auth itu sendiri
        // (login/token) agar tidak memicu loop reload saat kredensial salah.
        const url = originalRequest.url || '';
        const isAuthEndpoint = url.includes('/login') || url.includes('/token');

        // If the error status is 401 and there is no originalRequest._retry flag,
        // it means the token has expired and we need to refresh it
        if (error.response.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
            // Cegah concurrent refresh calls:
            // request yang datang saat refresh berjalan MASUK ANTRIAN
            // (bukan langsung ditolak), lalu di-retry setelah token baru siap.
            if (isRefreshing) {
                originalRequest._retry = true;
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then((token) => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return api(originalRequest);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Browser: refresh token sudah otomatis terkirim via cookie (withCredentials: true).
                // Pakai authApi (raw instance, tanpa interceptor) agar refresh TIDAK
                // memicu dirinya sendiri / loop.
                const response = await authApi.post('/token');
                const token = response.data.token;

                localStorage.setItem('token', token);

                // Sync juga header default axios (untuk authProvider)
                axios.defaults.headers.common["Authorization"] = "Bearer " + token;

                // Retry semua request yang menunggu di antrian
                processQueue(null, token);

                // Retry the original request with the new token
                originalRequest.headers.Authorization = `Bearer ${token}`;

                return api(originalRequest);
            } catch (refreshError) {
                // gagal refresh -> tolak juga semua yang menunggu di antrian
                processQueue(refreshError, null);

                console.log('Token refresh failed:', refreshError?.response || refreshError?.message);
                localStorage.removeItem('token');
                delete axios.defaults.headers.common["Authorization"];

                // Hanya reload bila sedang di halaman yang butuh auth.
                // Jika sudah di /login, cukup biarkan user login ulang (tanpa reload
                // yang membuat pesan error di console hilang).
                if (!window.location.pathname.startsWith('/login')) {
                    window.location.href = '/login';
                }
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

export default api;